import {
  Action,
  combineReducers,
  configureStore,
  ConfigureStoreOptions,
  EnhancedStore,
  Reducer,
  StateFromReducersMapObject
} from '@reduxjs/toolkit'
import createSagaMiddleware, { Saga } from 'redux-saga'
import { ActionTypes } from '@carto/kepler.gl/actions'
import { taskMiddleware } from 'react-palm/tasks'
import { addLayerRequest } from 'features/builder/state/builderSlice'
import { _pushDialog } from 'features/dialogs/state/dialogsSlice'
import { createImportingJobRequest } from 'features/jobs/state/jobsImportingSlice'
import reducer from './reducer'
import rootSaga from './sagas'
import trackJSMiddleware from './trackJSMiddleware'
import { registerStore } from './storeRegistry'
import settings from 'settings'
import { QueryClient } from 'react-query'
import { defaultQueryClient } from 'queryClient'

export type AppStore = EnhancedStore<StateFromReducersMapObject<typeof reducer>>

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>

/**
 * App store with cleanup and access to running sagas.
 */
export type EnhancedAppStore = {
  queryClient: QueryClient
  store: AppStore
  reducer: Reducer<RootState, Action>
  destroy: () => Promise<void>
  runAppSaga: <T, TReturn, TNext>(saga: () => Generator<T, TReturn, TNext>, signal?: AbortSignal) => Promise<TReturn>
}

export function createAppStore(preloadedState = {}, queryClient: QueryClient): EnhancedAppStore {
  const sagaMiddleware = createSagaMiddleware({
    context: {
      queryClient
    }
  })

  const configureMiddleware: ConfigureStoreOptions['middleware'] = (getDefaultMiddleware) => {
    const middleware = [
      ...getDefaultMiddleware({
        thunk: false,
        serializableCheck: {
          ignoredPaths: ['keplerGl', 'dialogs', 'workflows'],
          ignoredActions: [
            ...(Object.values(ActionTypes) as string[]),
            addLayerRequest.type,
            createImportingJobRequest.type,
            _pushDialog.type,
            '$$test/replaceStateActionType'
          ],
          ignoredActionPaths: ['payload.factory'],
          warnAfter: 128
        },
        immutableCheck: {
          ignoredPaths: ['keplerGl'],
          warnAfter: 128
        }
      }),
      taskMiddleware,
      sagaMiddleware
    ]

    if (settings.enableTrackJs) {
      middleware.push(trackJSMiddleware)
    }

    return middleware
  }

  let canceled = false
  const actualReducer = combineReducers(reducer)
  const store = configureStore({
    reducer: actualReducer,
    middleware: configureMiddleware,
    preloadedState,
    devTools: { autoPause: true, shouldRecordChanges: false }
  })

  const sagasTask = sagaMiddleware.run(rootSaga as Saga)

  return {
    store,
    reducer: actualReducer,
    queryClient,
    runAppSaga: <T, TReturn, TNext>(saga: () => Generator<T, TReturn, TNext>, signal?: AbortSignal) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const task = sagaMiddleware.run(saga as any)
      if (signal) {
        signal.onabort = () => task.cancel()
      }
      return task.toPromise<TReturn>()
    },
    destroy: async () => {
      if (!canceled) {
        sagasTask.cancel()
        canceled = true
      }
      await sagasTask.toPromise()
    }
  }
}

export const { store, destroy, runAppSaga } = createAppStore(undefined, defaultQueryClient)

registerStore(store)
